import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react'
import toast from 'react-hot-toast'

import Modal from '@/components/common/Modal'
import BiConnectionsPanel from '@/components/connections/BiConnectionsPanel'
import ReferenceSourceFields, {
  emptyReferenceSource, referenceSourceError, referenceSourceLabel,
  type ReferenceSourceValue,
} from '@/components/sources/ReferenceSourceFields'
import { listBiConnections, type BiConnection } from '@/api/connections'
import {
  listProjectDbConnections, type ProjectDbConnection,
} from '@/api/projectDb'
import {
  registerGitSource, registerLocalPathSource, uploadZipSource,
} from '@/api/referenceSources'

/**
 * "Add a source" — the Ask-AI composer's structured alternative to describing
 * a source in prose.
 *
 * The build agent detects a source from the prompt on its own; this just makes
 * it unambiguous (and lets the user CREATE the connection without leaving the
 * chat). Every type reuses the UI that already exists for it — the Analytics
 * connections panel, the workbench's reference-source fields — rather than
 * growing a second way to define the same thing.
 *
 * On confirm the source is registered through its normal API and the caller
 * gets an AttachedSource to show as a chip; the chip's `promptLine` is
 * prepended to the message so detection resolves the exact source instead of
 * inferring one.
 */

export type SourceKind = 'database' | 'git' | 'zip' | 'ioflow' | 'spec'

export interface AttachedSource {
  kind: SourceKind
  /** Chip label. */
  label: string
  /** Machine-readable line prepended to the outgoing prompt. */
  promptLine: string
}

export const SOURCE_TYPES: {
  kind: SourceKind; label: string; icon: string; hint: string
}[] = [
  // NOTE: icons must be classic **Material Icons** ligatures — index.html loads
  // `Material+Icons`, NOT Material Symbols. An unknown name (e.g. 'database')
  // renders as the literal word in the icon font.
  { kind: 'database', label: 'Database', icon: 'storage',
    hint: 'Postgres, MySQL, Oracle, SQL Server, Snowflake, BigQuery, Dremio, SAP HANA' },
  { kind: 'ioflow', label: 'IOFlow connector', icon: 'hub',
    hint: 'Google Sheets, Notion, Airtable — connected in Integrations' },
  { kind: 'git', label: 'Git source', icon: 'code',
    hint: 'Replicate an existing application from its repository' },
  { kind: 'zip', label: 'Source zip', icon: 'folder_zip',
    hint: 'Replicate an existing application from an uploaded archive' },
  { kind: 'spec', label: 'Specification file', icon: 'description',
    hint: 'Attach a requirements document to build from' },
]

/** Radio key for a project-database connection.
 *
 * The two stores are separate namespaces, so a project-db id could collide
 * with an Analytics CONNECTION_ID; prefixing keeps one selection state for
 * both lists without either shadowing the other. */
const appConnKey = (c: ProjectDbConnection) => `app:${c.id}`

const groupHeadStyle: CSSProperties = {
  margin: '0.25rem 0 0',
  fontSize: '0.8125rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  color: 'var(--color-text-secondary)',
}

const connRowStyle = (selected: boolean): CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.5rem 0.75rem',
  cursor: 'pointer',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  background: selected ? 'var(--color-bg-secondary)' : 'transparent',
})

export default function AddSourceModal({
  open, kind, project, onClose, onAttach, onPickSpecFile,
}: {
  open: boolean
  kind: SourceKind | null
  project: string
  onClose: () => void
  onAttach: (source: AttachedSource) => void
  /** Spec files reuse the composer's own file picker. */
  onPickSpecFile?: () => void
}) {
  const [connections, setConnections] = useState<BiConnection[]>([])
  // The project's OWN database connections (postgres/oracle/sqlite). These are
  // a different store from the Analytics ones above and the distinction is not
  // cosmetic: only these can become the application's database and have their
  // tables adopted, so an app built ON a customer database must attach one of
  // these, never an Analytics connection.
  const [appConnections, setAppConnections] = useState<ProjectDbConnection[]>([])
  const [selectedConn, setSelectedConn] = useState('')
  const [manageOpen, setManageOpen] = useState(false)
  // "New connection…" opens the hub straight on the blank form; the saved
  // connection is auto-selected so one more click attaches it.
  const [manageStartNew, setManageStartNew] = useState(false)
  const [refSource, setRefSource] = useState<ReferenceSourceValue>(emptyReferenceSource())
  const [uploadPct, setUploadPct] = useState(0)
  const [busy, setBusy] = useState(false)
  const [ioflowUrl, setIoflowUrl] = useState('')
  const [ioflowConnector, setIoflowConnector] = useState('google_sheets')
  // Guards a late listBiConnections response from a closed/reopened modal.
  const reqRef = useRef(0)

  const refreshConnections = useCallback(async () => {
    const seq = ++reqRef.current
    // Both stores, independently: one being unavailable must not hide the
    // other, or the user is told "no connections" while half of them exist.
    const [bi, app] = await Promise.allSettled([
      listBiConnections(project),
      listProjectDbConnections(project),
    ])
    if (seq !== reqRef.current) return
    if (bi.status === 'fulfilled') setConnections(bi.value)
    if (app.status === 'fulfilled') setAppConnections(app.value)
    const failed = [bi, app].find((r) => r.status === 'rejected') as
      PromiseRejectedResult | undefined
    if (failed) {
      const err = failed.reason
      toast.error(`Could not load connections: ${err instanceof Error ? err.message : err}`)
    }
    const rows = bi.status === 'fulfilled' ? bi.value : []
    const apps = app.status === 'fulfilled' ? app.value : []
    setSelectedConn((prev) => {
      if (prev) return prev
      const total = rows.length + apps.length
      if (total !== 1) return ''
      return apps.length === 1 ? appConnKey(apps[0]) : rows[0].CONNECTION_ID
    })
  }, [project])

  useEffect(() => {
    if (!open) return
    setBusy(false)
    setUploadPct(0)
    if (kind === 'database') void refreshConnections()
    if (kind === 'git') setRefSource(emptyReferenceSource('git'))
    if (kind === 'zip') setRefSource(emptyReferenceSource('zip'))
  }, [open, kind, refreshConnections])

  // The spec-file type has no UI of its own — hand straight off to the
  // composer's existing attachment picker.
  useEffect(() => {
    if (open && kind === 'spec') {
      onClose()
      onPickSpecFile?.()
    }
  }, [open, kind, onClose, onPickSpecFile])

  const attachDatabase = () => {
    // A PROJECT DATABASE connection: the app can be built ON it — its tables
    // adopted in place, or its schema reused for the project's own copy. The
    // prompt line says so, because that routing decision is the agent's first
    // one and it cannot be recovered from the id alone.
    const appConn = appConnections.find((c) => appConnKey(c) === selectedConn)
    if (appConn) {
      onAttach({
        kind: 'database',
        label: `${appConn.name} (${appConn.db_type})`,
        promptLine:
          `[Data source: the registered PROJECT DATABASE connection ` +
          `"${appConn.name}", type ${appConn.db_type}` +
          `${appConn.db_schema ? `, schema ${appConn.db_schema}` : ''}` +
          '. This is an application database, so the app can be built ON it: ' +
          'explore it before planning, then confirm the adoption mode with me ' +
          '— "source" (this database becomes the application\'s own and its ' +
          'tables are used in place) or "structure" (reuse only the table ' +
          'design; the application creates and owns its own copy).]',
      })
      onClose()
      return
    }
    const conn = connections.find((c) => c.CONNECTION_ID === selectedConn)
    if (!conn) { toast.error('Choose a connection'); return }
    const name = conn.CONNECTION_NAME || conn.CONNECTION_ID
    onAttach({
      kind: 'database',
      label: `${name}${conn.DATABASE_TYPE ? ` (${conn.DATABASE_TYPE})` : ''}`,
      promptLine:
        `[Data source: the registered database connection "${conn.CONNECTION_ID}"` +
        `${conn.DATABASE_TYPE ? `, type ${conn.DATABASE_TYPE}` : ''}` +
        `${conn.SCHEMA ? `, schema ${conn.SCHEMA}` : ''}` +
        '. Explore it before planning.]',
    })
    onClose()
  }

  const attachReferenceSource = async () => {
    const problem = referenceSourceError(refSource)
    if (problem) { toast.error(problem); return }
    setBusy(true)
    try {
      if (refSource.mode === 'git') {
        await registerGitSource(project, refSource.gitUrl.trim(), refSource.gitRef.trim())
      } else if (refSource.mode === 'path') {
        await registerLocalPathSource(project, refSource.localPath.trim())
      } else {
        await uploadZipSource(project, refSource.zipFile as File, setUploadPct)
      }
      const label = referenceSourceLabel(refSource)
      onAttach({
        kind: refSource.mode === 'zip' ? 'zip' : 'git',
        label,
        promptLine:
          `[Reference source registered: ${label}. Replicate this application ` +
          'from its source code — analyze it before planning.]',
      })
      toast.success('Source registered')
      onClose()
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail
      toast.error(detail || `Could not register the source: ${err instanceof Error ? err.message : err}`)
    } finally {
      setBusy(false)
      setUploadPct(0)
    }
  }

  const attachIoflow = () => {
    const url = ioflowUrl.trim()
    if (ioflowConnector === 'google_sheets' && !url) {
      toast.error('Paste the Google Sheet link')
      return
    }
    onAttach({
      kind: 'ioflow',
      label: url || ioflowConnector,
      promptLine:
        `[Data source: the IOFlow connector "${ioflowConnector}"` +
        `${url ? ` — ${url}` : ''}. Explore it before planning.]`,
    })
    onClose()
  }

  if (!open || !kind || kind === 'spec') return null

  const title = {
    database: 'Add a database source',
    git: 'Add a git source',
    zip: 'Add a source zip',
    ioflow: 'Add an IOFlow source',
  }[kind]

  return (
    <>
      <Modal open={open && !manageOpen} onClose={onClose} title={title}>
        {kind === 'database' && (
          <div>
            <p style={{ fontSize: '0.9375rem', color: 'var(--color-text-secondary)', marginTop: 0 }}>
              Pick the connection to build on. Its tables are explored before any
              scope or specification is proposed.
            </p>
            {connections.length === 0 && appConnections.length === 0 ? (
              <p style={{ fontSize: '0.9375rem' }}>
                No connections yet — create one to continue.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                {appConnections.length > 0 && (
                  <>
                    <p style={groupHeadStyle}>
                      Application database — the app can read and write it, or
                      reuse its table design
                    </p>
                    {appConnections.map((c) => (
                      <label key={appConnKey(c)} style={connRowStyle(selectedConn === appConnKey(c))}>
                        <input
                          type="radio"
                          name="add-source-connection"
                          checked={selectedConn === appConnKey(c)}
                          onChange={() => setSelectedConn(appConnKey(c))}
                        />
                        <span style={{ fontWeight: 500 }}>{c.name}</span>
                        <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                          {c.db_type}{c.db_schema ? ` · ${c.db_schema}` : ''}
                          {c.role === 'preview_runtime' ? " · this project's database" : ''}
                        </span>
                      </label>
                    ))}
                  </>
                )}
                {connections.length > 0 && (
                  <>
                    <p style={groupHeadStyle}>
                      Analytics source — read-only; reports and dashboards only
                    </p>
                    {connections.map((c) => (
                      <label key={c.CONNECTION_ID} style={connRowStyle(selectedConn === c.CONNECTION_ID)}>
                        <input
                          type="radio"
                          name="add-source-connection"
                          checked={selectedConn === c.CONNECTION_ID}
                          onChange={() => setSelectedConn(c.CONNECTION_ID)}
                        />
                        <span style={{ fontWeight: 500 }}>
                          {c.CONNECTION_NAME || c.CONNECTION_ID}
                        </span>
                        <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                          {c.DATABASE_TYPE}{c.SCHEMA ? ` · ${c.SCHEMA}` : ''}
                        </span>
                      </label>
                    ))}
                  </>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                className="btn btn-sm"
                onClick={() => { setManageStartNew(true); setManageOpen(true) }}
              >
                New connection…
              </button>
              <button className="btn btn-sm" onClick={() => setManageOpen(true)}>
                Manage connections…
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={attachDatabase}
                disabled={!selectedConn}
              >
                Use this connection
              </button>
            </div>
          </div>
        )}

        {(kind === 'git' || kind === 'zip') && (
          <div>
            <ReferenceSourceFields
              value={refSource}
              onChange={setRefSource}
              disabled={busy}
              uploadPct={uploadPct}
              modes={kind === 'zip' ? ['zip'] : ['git', 'path']}
            />
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-sm" onClick={onClose} disabled={busy}>Cancel</button>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => void attachReferenceSource()}
                disabled={busy}
              >
                {busy ? 'Registering…' : 'Register source'}
              </button>
            </div>
          </div>
        )}

        {kind === 'ioflow' && (
          <div>
            <p style={{ fontSize: '0.9375rem', color: 'var(--color-text-secondary)', marginTop: 0 }}>
              IOFlow connectors are authorised in Integrations. Choose the
              connector and, for a spreadsheet, paste its link.
            </p>
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="ioflow-connector">Connector</label>
              <select
                id="ioflow-connector"
                value={ioflowConnector}
                onChange={(e) => setIoflowConnector(e.target.value)}
              >
                <option value="google_sheets">Google Sheets</option>
                <option value="notion">Notion</option>
                <option value="airtable">Airtable</option>
                <option value="postgresql">PostgreSQL (via IOFlow)</option>
                <option value="mysql">MySQL (via IOFlow)</option>
              </select>
            </div>
            <div style={{ marginBottom: '1.25rem' }}>
              <label htmlFor="ioflow-url">Link or identifier</label>
              <input
                id="ioflow-url"
                type="text"
                value={ioflowUrl}
                onChange={(e) => setIoflowUrl(e.target.value)}
                placeholder="https://docs.google.com/spreadsheets/d/…"
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-sm" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={attachIoflow}>
                Use this source
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* The Analytics connections hub itself — the SAME editor the Analytics
          tab uses, so a connection created here behaves identically. */}
      {manageOpen && (
        <BiConnectionsPanel
          project={project}
          variant="modal"
          open={manageOpen}
          initialNew={manageStartNew}
          onClose={() => {
            setManageOpen(false)
            setManageStartNew(false)
            void refreshConnections()
          }}
          onChanged={(saved) => {
            // Auto-select what was just saved so "Use this connection" is
            // one click away when the hub closes.
            if (saved?.CONNECTION_ID) setSelectedConn(saved.CONNECTION_ID)
            void refreshConnections()
          }}
        />
      )}
    </>
  )
}
